import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Briefcase, Plus, Pencil, Trash2, X, Loader2, Users, ShieldCheck, ArrowLeft,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";

const COLORS = ["#3b82f6", "#22c55e", "#f59e0b", "#ec4899", "#a855f7", "#38bdf8", "#ef4444", "#f7c935"];

function PosteEditor({ api, poste, catalog, onClose, onSaved }) {
  const isNew = !poste?.poste_id;
  const [name, setName] = useState(poste?.name || "");
  const [description, setDescription] = useState(poste?.description || "");
  const [color, setColor] = useState(poste?.color || "#3b82f6");
  const [grants, setGrants] = useState(new Set(poste?.grants || []));
  const [saving, setSaving] = useState(false);

  const toggleGrant = (key) => {
    setGrants((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleModule = (perms, allOn) => {
    setGrants((prev) => {
      const next = new Set(prev);
      perms.forEach((p) => { if (!p.admin_only) { if (allOn) next.delete(p.key); else next.add(p.key); } });
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Nom du poste requis"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description, color, grants: Array.from(grants) };
      if (isNew) {
        await api.post("/tenant/postes", payload);
        toast.success(`Poste « ${name.trim()} » créé`);
      } else {
        const r = await api.put(`/tenant/postes/${poste.poste_id}`, payload);
        const n = r.data?.updated_members || 0;
        toast.success(n > 0 ? `Poste mis à jour — droits propagés à ${n} salarié(s)` : "Poste mis à jour");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="poste-editor-dialog">
      <div className="bg-[#0f0a18] border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-bold text-white flex items-center gap-2">
            <Briefcase className="w-4 h-4" style={{ color }} />
            {isNew ? "Nouveau poste" : `Modifier — ${poste.name}`}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-slate-400" data-testid="poste-editor-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Nom du poste *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Assistant commercial"
                className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#f7c935]/40 outline-none"
                data-testid="poste-name-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Couleur</label>
              <div className="flex gap-2 pt-1.5">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setColor(c)}
                    className={`w-6 h-6 rounded-full transition-transform ${color === c ? "ring-2 ring-white scale-110" : "hover:scale-110"}`}
                    style={{ background: c }} data-testid={`poste-color-${c.slice(1)}`} />
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Mission du poste"
              className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#f7c935]/40 outline-none"
              data-testid="poste-description-input" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Droits du poste — {grants.size} activé(s)</p>
            </div>
            <div className="space-y-3">
              {catalog.map((mod) => {
                const editable = mod.permissions.filter((p) => !p.admin_only);
                const onCount = editable.filter((p) => grants.has(p.key)).length;
                const allOn = editable.length > 0 && onCount === editable.length;
                return (
                  <div key={mod.module} className="rounded-lg border border-white/5 bg-white/[0.02] overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-white/[0.03]">
                      <span className="text-xs font-bold text-white">{mod.label}</span>
                      <button onClick={() => toggleModule(mod.permissions, allOn)}
                        className="text-[10px] text-[#f7c935] hover:underline" data-testid={`poste-module-toggle-${mod.module}`}>
                        {allOn ? "Tout retirer" : "Tout accorder"}
                      </button>
                    </div>
                    <div className="divide-y divide-white/5">
                      {mod.permissions.map((p) => (
                        <div key={p.key} className={`flex items-center justify-between px-3 py-1.5 ${p.admin_only ? "opacity-40" : ""}`}>
                          <span className="text-xs text-slate-300">{p.label}{p.admin_only && <span className="text-[9px] text-amber-400 ml-1.5">admin uniquement</span>}</span>
                          <Switch checked={grants.has(p.key)} disabled={p.admin_only}
                            onCheckedChange={() => toggleGrant(p.key)} data-testid={`poste-perm-${p.key}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/10">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-slate-300 h-9 text-xs">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-9 text-xs" data-testid="save-poste-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isNew ? "Créer le poste" : "Enregistrer et propager"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PostesPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [postes, setPostes] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | poste

  const load = async () => {
    setLoading(true);
    try {
      const [pr, cr] = await Promise.all([
        api.get("/tenant/postes"),
        api.get("/tenant/permissions/catalog"),
      ]);
      setPostes(pr.data?.items || []);
      setCatalog(cr.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger les postes");
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const removePoste = async (p) => {
    const msg = p.member_count > 0
      ? `Supprimer le poste « ${p.name} » ?\n\n${p.member_count} salarié(s) seront détachés (leurs droits actuels sont conservés).`
      : `Supprimer le poste « ${p.name} » ?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/tenant/postes/${p.poste_id}`);
      toast.success("Poste supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="p-6 max-w-[1000px] mx-auto" data-testid="postes-page">
      <button onClick={() => navigate("/tenant/team")} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-3" data-testid="back-to-team">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour à Mon équipe
      </button>
      <PageHeader
        icon={<Briefcase />}
        title="Postes & droits"
        description="Créez vos postes, définissez leurs droits : chaque salarié rattaché hérite automatiquement des droits de son poste."
        testId="postes-header"
        actions={(
          <Button onClick={() => setEditing({})} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="new-poste-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouveau poste
          </Button>
        )}
      />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {postes.map((p) => (
            <div key={p.poste_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/15 transition-colors" data-testid={`poste-card-${p.poste_id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${p.color}18` }}>
                  <Briefcase className="w-5 h-5" style={{ color: p.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-sm truncate">{p.name}</p>
                  <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{p.description || "—"}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(p)} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400" title="Modifier" data-testid={`edit-poste-${p.poste_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => removePoste(p)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-400" title="Supprimer" data-testid={`delete-poste-${p.poste_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/5">
                <span className="flex items-center gap-1 text-[11px] text-slate-400">
                  <ShieldCheck className="w-3.5 h-3.5" style={{ color: p.color }} /> {(p.grants || []).length} droits
                </span>
                <span className="flex items-center gap-1 text-[11px] text-slate-400" data-testid={`poste-members-${p.poste_id}`}>
                  <Users className="w-3.5 h-3.5" /> {p.member_count} salarié(s)
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <PosteEditor api={api} poste={editing.poste_id ? editing : null} catalog={catalog}
          onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}
